import { ComboType, OrderItem } from '@oolio-group/domain';
import { useCurrency, useTranslation } from '@oolio-group/localization';
import {
  computeOrderItemTotal,
  getAdjustmentLabel,
} from '@oolio-group/order-helper';
import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import theme from '../../common/theme';
import { themeType } from '../../common/theme-colors';
import { useTheme } from '../../hooks/useTheme';

export interface ItemProps {
  item: OrderItem;
}

const CartItem: React.FC<ItemProps> = ({ item }) => {
  const { formatCurrency } = useCurrency();
  const { translate } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const renderModifiers = (orderItem: OrderItem, isCombo = false) => {
    return orderItem.modifiers?.map(modifier => {
      const comboProductQuantity = isCombo ? orderItem.quantity ?? 1 : 1;

      const sum =
        modifier.unitPrice *
        (modifier?.quantity * comboProductQuantity * item.quantity);
      return (
        <View style={styles.rowStyle} key={modifier.id}>
          <Text style={styles.smallText} testID="order-item-modifiers-name">
            {modifier.quantity ?? 1} x {modifier.name}
          </Text>
          <Text style={styles.smallText} testID="order-item-modifiers-price">
            {formatCurrency(sum)}
          </Text>
        </View>
      );
    });
  };

  const renderComboItem = () => {
    return item?.comboItems?.map(comboItem => {
      const sum =
        (comboItem.unitPrice ?? 0) *
        ((comboItem?.quantity ?? 1) * (item.quantity ?? 1));
      return (
        <>
          <View style={styles.rowStyle} key={comboItem.id}>
            <Text style={styles.comboName} testID="order-item-combo">
              {item.product?.comboType === ComboType.PORTION
                ? comboItem.product?.name
                : `${comboItem?.quantity ?? 1} x ${comboItem.product?.name}`}
            </Text>
            <Text style={styles.smallText} testID="order-item-modifiers-price">
              {formatCurrency(sum)}
            </Text>
          </View>
          {!!comboItem.notes && (
            <Text style={styles.smallText} testID="combo-item-notes">
              {translate('cdsCartScreen.itemNote', {
                note: comboItem.notes.trim(),
              })}
            </Text>
          )}
          {renderModifiers(comboItem as OrderItem, true)}
        </>
      );
    });
  };

  const renderAdjustments = (
    adjustmentAmount: number,
    label: string,
    testID: string,
  ) => {
    return (
      typeof adjustmentAmount === 'number' &&
      adjustmentAmount > 0 && (
        <View style={styles.rowStyle}>
          <Text style={styles.smallText}>{label}</Text>
          <Text style={styles.smallText} testID={testID}>
            {formatCurrency(adjustmentAmount)}
          </Text>
        </View>
      )
    );
  };

  return (
    <View style={styles.itemContainer}>
      <View style={styles.rowStyle}>
        <Text style={styles.largeText} testID="order-item-name-quantity">
          {`${item?.quantity > 1 ? `${item?.quantity} x ` : ''}${
            !!item.variant ? item.variant?.name : item.product?.name
          }`}
        </Text>
        <Text style={styles.largeText} testID="order-item-price">
          {formatCurrency(computeOrderItemTotal(item))}
        </Text>
      </View>
      {!!item.variant && (
        <View style={styles.rowStyle}>
          <Text style={styles.smallText} testID="order-item-variant">
            {item.product.name.replace(`${item.variant.name}-`, '')}
          </Text>
        </View>
      )}
      {renderModifiers(item)}
      <View>
        {renderComboItem()}
        {!!item.savedAmount && item.savedAmount > 0 && (
          <Text style={styles.messageSave}>
            {translate('cdsCartScreen.saveMessage', {
              savedAmount: formatCurrency(
                item?.savedAmount * (item?.quantity ?? 1),
              ),
              productName: item.product?.name,
            })}
          </Text>
        )}
      </View>
      {renderAdjustments(
        item.discountAmount,
        getAdjustmentLabel(item.adjustments?.[0]) ||
          translate('order.discount'),
        'order-item-discount',
      )}
      {renderAdjustments(
        item.surchargeAmount || 0,
        getAdjustmentLabel(item.adjustments?.[0]) ||
          translate('order.surcharge'),
        'order-item-surcharge',
      )}
      {!!item.notes && (
        <Text style={styles.smallText} testID="order-item-notes">
          {translate('cdsCartScreen.itemNote', { note: item.notes.trim() })}
        </Text>
      )}
    </View>
  );
};

const makeStyles = (colors: themeType) =>
  StyleSheet.create({
    itemContainer: {
      paddingVertical: theme.spacing.medium,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderColor,
      paddingRight: theme.spacing.medium,
    },
    rowStyle: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    smallText: {
      fontSize: theme.fontSize.small,
      color: colors.textLight,
      lineHeight: 22,
    },
    largeText: {
      fontWeight: '600',
      fontSize: theme.fontSize.large,
      color: colors.text,
      lineHeight: 40,
    },
    comboName: {
      fontWeight: '400',
      fontSize: theme.fontSize.small,
      color: colors.text,
      lineHeight: 22,
    },
    messageSave: {
      fontWeight: '600',
      fontSize: theme.fontSize.small,
      color: colors.blue,
      marginTop: 10,
    },
  });

export default CartItem;
